import { useContext, useEffect, useState } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { scrollToTop, setCountryCode } from '../../common/HelperMethods';
import {
  InsuranceLoadingMessage,
  PageAbsolutePath
} from '../../../consts/Common';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState,
  InsuranceOptionalProduct,
  QuoteStatus
} from '../../common/CommonTypes';
import InsuranceSubProduct from '../../common/InsuranceProduct/InsuranceSubProduct';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { formatNumber } from '../../common/Formatter';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import { useErrorBoundary } from 'react-error-boundary';

const OptionalCoverPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const {
    insuranceProducts,
    drivingHistoryDetails,
    staticData,
    insuranceNavigationStatus,
    savingQuote
  } = journey;
  const { ncdDiscount } = drivingHistoryDetails;
  const { commonEvents, optionalCoverPage, drivingHistoryDetailsPage } =
    pageEvents;
  const { setSavingQuote, onPageChange, setQuote } = commonEvents;
  const { onUpdateSelectedOptionalProduct } = optionalCoverPage;
  const { onChangeNcdDiscount } = drivingHistoryDetailsPage;
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const [loading, setLoading] = useState(false);
  const [originalProductJson, setOriginalProductJson] = useState('');
  const [originalNCDValue] = useState(ncdDiscount);
  const { saveQuote } = useCarInsuranceController();
  const hasProductSelectionChanged = (): boolean => {
    const currentJson = JSON.stringify(insuranceProducts);
    const hasChanged =
      currentJson !== originalProductJson || ncdDiscount !== originalNCDValue;

    return hasChanged;
  };

  useEffect(() => {
    scrollToTop();
    const productJson = JSON.stringify(insuranceProducts);
    setOriginalProductJson(productJson);
  }, []);

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const onBackClick = () => {
    if (hasProductSelectionChanged()) {
      setLoading(true);
      saveQuote(
        { ...journey, quoteStatus: QuoteStatus.Full },
        site.user.token,
        saveFullQuoteSuccessAndGoBack,
        onBackendFailure
      );
    } else {
      redirectUser(PageAbsolutePath.Insurance.YourQuote);
    }
  };

  const saveFullQuoteSuccess = (quote: InsuranceJourneyState) => {
    setLoading(false);
    console.log('Optional product page : ', quote);
    setQuote({
      ...quote,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });
    redirectUser(PageAbsolutePath.Insurance.CheckYourDetails);
  };

  const saveFullQuoteSuccessAndGoBack = (quote: InsuranceJourneyState) => {
    setLoading(false);
    console.log('Optional product page : ', quote);
    setQuote({
      ...quote,
      staticData: [...staticData],
      insuranceNavigationStatus: { ...insuranceNavigationStatus }
    });
    redirectUser(PageAbsolutePath.Insurance.YourQuote);
  };

  const onNextClick = () => {
    if (hasProductSelectionChanged()) {
      setLoading(true);
      saveQuote(
        { ...journey, quoteStatus: QuoteStatus.Full },
        site.user.token,
        saveFullQuoteSuccess,
        onBackendFailure
      );
    } else {
      redirectUser(PageAbsolutePath.Insurance.CheckYourDetails);
    }
  };

  const redirectUser = (page: string) => {
    const path: string = setCountryCode(page, site.countryCode);
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.OptionalCover, true);
  };

  const updateOptionalProducts = (updatedProduct: InsuranceOptionalProduct) => {
    const updatedOptionalProducts: InsuranceOptionalProduct[] =
      insuranceProducts.optionalProductList.map((product) => {
        if (product.optionalProductId === updatedProduct.optionalProductId) {
          return updatedProduct;
        } else {
          return product;
        }
      });

    onUpdateSelectedOptionalProduct(updatedOptionalProducts);
  };

  const toggleNCDDiscount = () => {
    onChangeNcdDiscount(!ncdDiscount);
  };

  const ncdPrice = '£' + formatNumber(insuranceProducts.ncdPrice, 2, 'en-GB');

  const isEligibleForNCDDiscount =
    parseInt(drivingHistoryDetails.noClaimsBonusYear) >= 4;

  let totalCoverCost = 0;
  insuranceProducts.optionalProductList.forEach((product) => {
    if (product.isOptionalProductSelected) {
      if (product.optionalSubProductList.length > 0) {
        const selectedSubProduct = product.optionalSubProductList.find(
          (sp) => sp.isOptionalSubProductSelected
        );
        if (selectedSubProduct) {
          totalCoverCost += selectedSubProduct.optionalSubProductPrice;
        }
      } else {
        totalCoverCost += product.optionalProductPrice;
      }
    }
  });

  const formattedTotalCoverCost =
    '£' + formatNumber(totalCoverCost, 2, 'en-GB');

  return (
    <LoadingContainer loading={loading} message={InsuranceLoadingMessage}>
      <div className='one-col'>
        <ButtonCommand
          name='btn-back-top'
          text={'< Back'}
          onClick={onBackClick}
        />
      </div>
      <h4 className='section-header m3-top'>Optional cover for your vehicle</h4>
      <div className='one-col'>
        <p>
          Before you buy, have you thought about those little extras guaranteed
          to take care of all life’s what-ifs?
        </p>
      </div>
      {insuranceProducts.optionalProductList.map((product) => (
        <InsuranceSubProduct
          key={`optional-product-${product.optionalProductId}`}
          product={product}
          onProductChange={updateOptionalProducts}
        />
      ))}

      {insuranceProducts.optionalProductList.some(
        (op) => op.isOptionalProductSelected
      ) && (
        <>
          <h4 className='section-header m3-top'>Total optional cover cost</h4>
          <div className='one-col'>
            <p>
              Optional products are fully refundable if you cancel within 14
              days of its start date. Thereafter they are non-refundable.
            </p>
            <h4>{formattedTotalCoverCost}</h4>
          </div>
        </>
      )}
      {isEligibleForNCDDiscount && (
        <div className='one-col'>
          <h4 className='section-header m3-top'>
            No claims discount protection
          </h4>
          <p>
            Your NCD (No Claims Discount) years are valuable and provided you
            have 4 or more NCD years, you can protect them. You will need to
            provide proof of your NCD from your previous insurer.
          </p>
          <table className='mb-3'>
            <tr>
              <td>Important NCD Information</td>
              <td>{'View >'}</td>
            </tr>
            {drivingHistoryDetails.ncdDiscount === true &&
              insuranceProducts.ncdPrice > 0 && (
                <tr>
                  <td colSpan={2}>
                    <p>{ncdPrice}</p>
                  </td>
                </tr>
              )}
          </table>

          <ButtonContinue
            name='btn-add'
            text={ncdDiscount ? 'Remove' : 'Add'}
            onClick={toggleNCDDiscount}
            additionalClassNames='btn-ok'
          />
        </div>
      )}

      <div className='even-space-out flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
        <ButtonContinue
          name='btn-save-later'
          text='Save For Later'
          onClick={() => setSavingQuote(true)}
          additionalClassNames='btn-save'
          disabled={savingQuote}
        />
        <ButtonContinue
          name='btn-next'
          text='Next'
          onClick={onNextClick}
          additionalClassNames='btn-continue'
        />
      </div>
    </LoadingContainer>
  );
};

export default OptionalCoverPage;
